/**
 * P-08 授权码库存查询（docs/03-UIUX.md §5）。
 *
 * 跨批次检索授权码，按状态筛选、按产品过滤。
 * 服务端不存明文，只显示掩码（code_mask）。
 */
import { useCallback, useState } from 'react';
import { ErrorBlock, EmptyFiltered, EmptyFirstRun } from '../components/DataState';
import { Pager, DEFAULT_PAGE_SIZE, type PageSize } from '../components/Pager';
import { describeError } from '../api/client';
import { bulkVoidLicenseCodes, listLicenseCodes, listProducts, voidLicenseCode, type LicenseCode } from '../api/endpoints';
import { useAsync } from '../lib/useAsync';
import { formatDate } from '../lib/format';
import { Icon } from '../icons/Icon';
import { ICON_SIZE } from '../tokens';
import type { Product } from '../api/types';

const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'available', label: '可用' },
  { value: 'exported', label: '已导出' },
  { value: 'redeemed', label: '已兑换' },
  { value: 'void', label: '已作废' },
] as const;

/** 可作废的状态：未兑换（available/exported）。已兑换须走撤销授权链路。 */
const VOIDABLE: ReadonlySet<string> = new Set(['available', 'exported']);

function codeStatusLabel(s: string): string {
  return STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s;
}

export function CodesPage() {
  const products = useAsync(useCallback(() => listProducts(), []), []);
  const [filterProductId, setFilterProductId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [voidTarget, setVoidTarget] = useState<LicenseCode | null>(null);
  const [showBulkVoid, setShowBulkVoid] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());

  const hasActiveFilters = filterProductId !== '' || filterStatus !== '';

  const codes = useAsync(
    useCallback(() => {
      return listLicenseCodes({
        product_id: filterProductId || undefined,
        status: filterStatus || undefined,
        page,
        limit,
      });
    }, [filterProductId, filterStatus, page, limit]),
    [filterProductId, filterStatus, page, limit],
  );

  const productList: readonly Product[] = products.state.kind === 'ready' ? products.state.data.items : [];
  const codeList: readonly LicenseCode[] = codes.state.kind === 'ready' ? codes.state.data.items : [];
  const total = codes.state.kind === 'ready' ? codes.state.data.total : 0;
  const hasMore = codes.state.kind === 'ready' ? codes.state.data.hasMore : false;

  // 当前页可作废的行（批量选择只针对 VOIDABLE）
  const voidableOnPage = codeList.filter((c) => VOIDABLE.has(c.status));
  const allSelected = voidableOnPage.length > 0 && voidableOnPage.every((c) => selectedIds.has(c.id));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const c of voidableOnPage) next.delete(c.id);
      } else {
        for (const c of voidableOnPage) next.add(c.id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">库存查询</h1>
          <p className="page-head__sub">
            跨批次检索授权码。服务端不存明文，只能按状态和产品筛选。
          </p>
        </div>
      </div>

      <div className="filter-bar">
        <div className="filter-bar__group">
          <label className="filter-bar__label">产品</label>
          <select
            className="filter-bar__select"
            value={filterProductId}
            onChange={(e) => { setFilterProductId(e.target.value); setPage(1); }}
          >
            <option value="">全部产品</option>
            {productList.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="filter-bar__group">
          <label className="filter-bar__label">状态</label>
          <select
            className="filter-bar__select"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-muted" style={{ margin: '0 0 8px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name="shield-check" size={ICON_SIZE.inline} />
        掩码展示（安全设计）：明文仅在一次导出批次时流出；掩码头尾 5 位与明文一致，泄露时按此定位作废。
      </p>

      {codes.state.kind === 'failed' ? (
        <div className="table-frame">
          <ErrorBlock message={describeError(codes.state.error, '加载授权码')} onRetry={codes.reload} />
        </div>
      ) : codes.state.kind === 'loading' ? (
        <div className="table-frame">
          <table className="table">
            <TableSkeleton rows={10} columns={5} />
          </table>
        </div>
      ) : codeList.length === 0 && !hasActiveFilters ? (
        <div className="table-frame">
          <EmptyFirstRun
            icon="scan-barcode"
            description="暂无授权码。生成批次后授权码会出现在这里。"
          />
        </div>
      ) : codeList.length === 0 && hasActiveFilters ? (
        <div className="table-frame">
          <EmptyFiltered
            description="没有匹配的授权码，试试调整筛选条件。"
            onClearFilters={() => { setFilterProductId(''); setFilterStatus(''); setPage(1); }}
          />
        </div>
      ) : (
        <>
          <div className="table-frame">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      aria-label="全选本页可作废项"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={voidableOnPage.length === 0}
                    />
                  </th>
                  <th>授权码（掩码）</th>
                  <th>产品</th>
                  <th>批次</th>
                  <th>状态</th>
                  <th>创建时间</th>
                  <th>兑换时间</th>
                  <th className="table__actions-col">操作</th>
                </tr>
              </thead>
              <tbody>
                {codeList.map((code) => (
                  <tr key={code.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label="选择该授权码"
                        checked={selectedIds.has(code.id)}
                        onChange={() => toggleSelect(code.id)}
                        disabled={!VOIDABLE.has(code.status)}
                      />
                    </td>
                    <td>
                      <MaskCell mask={code.code_mask} />
                    </td>
                    <td>{productList.find((p) => p.id === code.product_id)?.name ?? code.product_id.slice(0, 8)}</td>
                    <td>
                      <code className="text-muted" title={code.batch_id} style={{ fontSize: 12 }}>{code.batch_id.slice(0, 8)}</code>
                    </td>
                    <td>
                      <span className={`status-dot status-dot--${code.status}`} />
                      {codeStatusLabel(code.status)}
                    </td>
                    <td>{formatDate(code.created_at)}</td>
                    <td>{code.redeemed_at ? formatDate(code.redeemed_at) : '-'}</td>
                    <td className="table__actions-col">
                      {VOIDABLE.has(code.status) ? (
                        <button
                          type="button"
                          className="btn btn--quiet btn--danger-text"
                          onClick={() => setVoidTarget(code)}
                          title="作废后不可兑换"
                        >
                          <Icon name="octagon-x" size={ICON_SIZE.inline} />
                          作废
                        </button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="filter-bar" style={{ marginTop: 12 }}>
            <div className="filter-bar__group">
              <span className="text-muted" style={{ fontSize: 13 }}>
                {selectedIds.size > 0 ? `已选 ${selectedIds.size} 条` : '勾选可作废项进行批量操作'}
              </span>
              {selectedIds.size > 0 && (
                <>
                  <button
                    type="button"
                    className="btn btn--quiet btn--danger-text"
                    onClick={() => setShowBulkVoid(true)}
                    disabled={selectedIds.size === 0}
                  >
                    <Icon name="octagon-x" size={ICON_SIZE.inline} />
                    批量作废（{selectedIds.size}）
                  </button>
                  <button type="button" className="btn btn--quiet" onClick={clearSelection}>
                    清空选择
                  </button>
                </>
              )}
            </div>
          </div>
          <Pager
            page={page}
            limit={limit}
            total={total}
            hasMore={hasMore}
            onPageChange={setPage}
            onLimitChange={(l) => { setLimit(l); setPage(1); }}
          />
        </>
      )}

      {voidTarget !== null && (
        <VoidDialog
          code={voidTarget}
          onClose={() => setVoidTarget(null)}
          onVoided={() => {
            setVoidTarget(null);
            codes.reload();
          }}
        />
      )}

      {showBulkVoid && (
        <BulkVoidDialog
          count={selectedIds.size}
          preview={codeList.filter((c) => selectedIds.has(c.id))}
          onClose={() => setShowBulkVoid(false)}
          onVoided={async (reason) => {
            await bulkVoidLicenseCodes([...selectedIds], reason);
            setShowBulkVoid(false);
            clearSelection();
            codes.reload();
          }}
        />
      )}
    </>
  );
}

function BulkVoidDialog({
  count,
  preview,
  onClose,
  onVoided,
}: {
  count: number;
  preview: readonly LicenseCode[];
  onClose: () => void;
  onVoided: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    if (!reason.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onVoided(reason.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : '批量作废失败');
      setSubmitting(false);
    }
  };

  const shown = preview.slice(0, 5);

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="批量作废">
        <div className="panel__head">
          <h2 className="panel__title">批量作废授权码</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <p className="text-muted">
            将批量作废选中的 <strong>{count}</strong> 条授权码（T2）。原子操作：任一条已兑换则整体拒绝；原因写入审计。
          </p>
          <div className="table-frame" style={{ border: 'none', padding: 0, marginBottom: 12 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>授权码（掩码）</th>
                  <th>批次</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => (
                  <tr key={c.id}>
                    <td><code style={{ fontSize: 12 }}>{c.code_mask}</code></td>
                    <td><code className="text-muted" style={{ fontSize: 12 }}>{c.batch_id.slice(0, 8)}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {count > shown.length && <p className="text-muted" style={{ fontSize: 12 }}>… 其余 {count - shown.length} 条省略</p>}
          <div className="field">
            <label>作废原因（必填）</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="如：录入错误 / 公开分享 / 退款" />
          </div>
          {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>取消</button>
            <button type="button" className="btn btn--danger" disabled={submitting || !reason.trim()} onClick={handle}>
              {submitting ? '作废中…' : '确认批量作废'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VoidDialog({
  code,
  onClose,
  onVoided,
}: {
  code: LicenseCode;
  onClose: () => void;
  onVoided: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    if (!reason.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await voidLicenseCode(code.id, reason.trim());
      onVoided();
    } catch (e) {
      setError(e instanceof Error ? e.message : '作废失败');
      setSubmitting(false);
    }
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="作废授权码">
        <div className="panel__head">
          <h2 className="panel__title">作废授权码</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <p className="text-muted">
            将作废授权码 <code style={{ fontSize: 12 }}>{code.code_mask}</code>（{codeStatusLabel(code.status)}，批次 <code style={{ fontSize: 12 }}>{code.batch_id.slice(0, 8)}</code>）。
            作废后该码不可再兑换，原因写入审计。
          </p>
          <p className="text-muted" style={{ fontSize: 12 }}>
            核对方式：导出的明文与掩码头尾 5 位一致，确认掩码与泄露的码吻合后再作废。
          </p>
          <div className="field">
            <label>作废原因（必填）</label>
            <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="如：录入错误 / 公开分享 / 退款" />
          </div>
          {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>取消</button>
            <button type="button" className="btn btn--danger" disabled={submitting || !reason.trim()} onClick={handle}>
              {submitting ? '作废中…' : '确认作废'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TableSkeleton({ rows, columns }: { rows: number; columns: number }) {
  return (
    <tbody aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <tr key={i}>
          {Array.from({ length: columns }, (_, j) => (
            <td key={j}><div className="skeleton-bar" style={{ width: `${[70, 45, 55, 40, 60, 50][j % 6]}%` }} /></td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

/** 掩码展示：完整 code_mask + 尾部识别组高亮（尾 5 位，与明文一致，用于泄露定位）。 */
function MaskCell({ mask }: { mask: string }) {
  const lastDash = mask.lastIndexOf('-');
  const tail = lastDash >= 0 ? mask.slice(lastDash) : '';
  const head = lastDash >= 0 ? mask.slice(0, lastDash) : mask;
  return (
    <code className="code-cell">
      <span className="text-muted">{head}</span>
      <span className="code-cell__tail">{tail}</span>
    </code>
  );
}